'use client';

import { useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Group, Popover, ScrollArea, Stack, Switch, Text } from '@mantine/core';
import { IconColumns } from '@tabler/icons-react';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import { KOLOM_PICKER_NEW_UNTIL } from '@constants/doc-pabean';
import { BLACK, WHITE, ensureContrast, hexToRgb } from '@utils/badge-contrast';
import {
  DOC_PABEAN_COLUMNS,
  type DocPabeanTablePageKey,
  isColumnHidden,
  isKolomPickerNew,
} from './doc-pabean-list-prefs';
import { useDocPabeanColumnToggle, useDocPabeanDisplayPrefs, useKolomSeen } from './use-doc-pabean-list-prefs';

/**
 * "Kolom": which doc-pabean table columns show on this page, and whether
 * company logos / country flags render (next-jarvis#961). Nomor Aju and
 * Actions are always shown, so they are not listed. Everything is stored per
 * page in this browser only.
 */
export default function DocPabeanColumnPicker({ pageKey }: { pageKey: DocPabeanTablePageKey }) {
  const { t } = useLanguage();
  const { colors: themeColors, isDark } = useTheme();
  const { columnsToggle, setColumnsToggle, resetColumnsToggle } = useDocPabeanColumnToggle(pageKey);
  const { prefs, setPref, resetPrefs } = useDocPabeanDisplayPrefs(pageKey);
  const { seen, markSeen } = useKolomSeen();
  const [opened, setOpened] = useState(false);

  // Evaluated per render rather than once per module load, so a tab left open
  // across the cut-off loses the chip on its next render.
  const showNew = !seen && isKolomPickerNew(new Date(), KOLOM_PICKER_NEW_UNTIL);

  // The sidebar's BARU chip: palette hue, nudged until it clears 4.5:1 on the
  // surface it sits on.
  const chip = useMemo(() => {
    const surface = hexToRgb(themeColors.card ?? (isDark ? '#1A221F' : '#FFFFFF'));
    const ink = ensureContrast(themeColors.accent ?? themeColors.primary ?? '#1D5FBF', surface, isDark ? WHITE : BLACK);
    return { color: ink, backgroundColor: `color-mix(in srgb, ${ink} 14%, transparent)` };
  }, [themeColors.card, themeColors.accent, themeColors.primary, isDark]);

  const optional = DOC_PABEAN_COLUMNS.filter((c) => c.toggleable);
  const allDefault = optional.every((c) => !isColumnHidden(c.accessor, columnsToggle)) && prefs.logos && prefs.flags;

  const toggle = (accessor: string, checked: boolean) => {
    setColumnsToggle((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const found = list.some((c) => c.accessor === accessor);
      if (found) return list.map((c) => (c.accessor === accessor ? { ...c, toggled: checked } : c));
      return [...list, { accessor, defaultToggle: true, toggleable: true, toggled: checked }];
    });
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md" width={300} withArrow>
      <Popover.Target>
        <Button
          variant="outline"
          data-testid="doc-pabean-kolom"
          leftSection={<IconColumns size={16} />}
          rightSection={
            showNew ? (
              <span
                data-testid="doc-pabean-kolom-new"
                className="px-1.5 py-0.5 text-[10px] font-semibold rounded-md uppercase tracking-wide"
                style={chip}
              >
                {t('badge.new')}
              </span>
            ) : undefined
          }
          onClick={() => {
            setOpened((o) => !o);
            markSeen();
          }}
          styles={{
            root: {
              borderColor: themeColors.border,
              color: themeColors.text,
              backgroundColor: 'transparent',
              borderRadius: '10px',
            },
            section: { color: themeColors.text },
          }}
        >
          {t('docpabean.kolom.button')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown style={{ backgroundColor: themeColors.card, borderColor: themeColors.border }}>
        <Text size="sm" fw={600} mb={2} style={{ color: themeColors.text }}>
          {t('docpabean.kolom.title')}
        </Text>
        <Text size="xs" mb={8} style={{ color: themeColors.textSecondary }}>
          {t('docpabean.kolom.alwaysShown')}
        </Text>
        <ScrollArea.Autosize mah={300} type="auto">
          <Stack gap={6}>
            {optional.map((c) => (
              <Checkbox
                key={c.accessor}
                size="sm"
                data-testid={`doc-pabean-kolom-${c.accessor}`}
                checked={!isColumnHidden(c.accessor, columnsToggle)}
                onChange={(e) => toggle(c.accessor, e.currentTarget.checked)}
                label={t(c.titleKey)}
                styles={{ label: { color: themeColors.text } }}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>
        <Divider color={themeColors.border} my={10} />
        <Text size="sm" fw={600} mb={6} style={{ color: themeColors.text }}>
          {t('docpabean.kolom.display')}
        </Text>
        <Stack gap={8}>
          <Switch
            size="sm"
            data-testid="doc-pabean-kolom-logos"
            checked={prefs.logos}
            onChange={(e) => setPref('logos', e.currentTarget.checked)}
            label={t('docpabean.kolom.showLogos')}
            styles={{ label: { color: themeColors.text } }}
          />
          <Switch
            size="sm"
            data-testid="doc-pabean-kolom-flags"
            checked={prefs.flags}
            onChange={(e) => setPref('flags', e.currentTarget.checked)}
            label={t('docpabean.kolom.showFlags')}
            styles={{ label: { color: themeColors.text } }}
          />
        </Stack>
        <Group justify="flex-end" mt={10}>
          <Button
            variant="subtle"
            size="compact-xs"
            data-testid="doc-pabean-kolom-reset"
            disabled={allDefault}
            onClick={() => {
              resetColumnsToggle();
              resetPrefs();
            }}
          >
            {t('docpabean.kolom.reset')}
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}
